import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useData } from '../context/DataContext';
import { db } from '../firebase';
import { sortEventsByStart } from './events';

const isEventResource = (resource) => {
  const type = String(resource?.type || '').toLowerCase();
  return type === 'event' || type === 'event-poster' || type === 'event_poster' || type === 'eventposter';
};
const EVENT_TYPES = ['event', 'event-poster', 'event_poster', 'eventposter'];

const isPublicEvent = (resource) => {
  if (!isEventResource(resource)) return false;
  if (resource?.eventAudience === 'all') return true;
  if (resource?.eventAudience === 'course') return false;
  if (resource?.postToHomepage === true) return true;
  if (resource?.postToHomepage === false) return !resource?.courseId;
  // Backward compatibility: older event uploads without audience flags should stay visible.
  return true;
};

export function usePublicEvents() {
  const { resources = [] } = useData();
  const [fallbackEvents, setFallbackEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';

  const resourceEvents = useMemo(
    () => sortEventsByStart(resources.filter((resource) => isPublicEvent(resource))),
    [resources]
  );

  useEffect(() => {
    if (!useFirebase || resourceEvents.length > 0) return;
    let cancelled = false;

    const loadEvents = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(query(collection(db, 'resources'), where('type', 'in', EVENT_TYPES)));
        if (cancelled) return;
        const next = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((resource) => isPublicEvent(resource));
        setFallbackEvents(sortEventsByStart(next));
      } catch (error) {
        if (!cancelled) {
          setFallbackEvents([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadEvents();

    return () => {
      cancelled = true;
    };
  }, [resourceEvents.length, useFirebase]);

  return {
    events: resourceEvents.length > 0 ? resourceEvents : fallbackEvents,
    loading: loading && resourceEvents.length === 0
  };
}
